import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Clock, X, ArrowRight, Tag, Layers } from 'lucide-react';
import { catalogApi } from '../api/catalog';
import type { SearchAutocompleteDto } from '../api/types';
import { money } from '../lib/format';

const HISTORY_KEY = 'ecommerce_search_history';
const MAX_HISTORY = 6;

export function SearchAutocomplete({
  className = '',
  placeholder = 'Ürün, marka, kategori, SKU veya barkod ara...',
  onCloseMobile,
}: {
  className?: string;
  placeholder?: string;
  onCloseMobile?: () => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: categoryTree } = useQuery({
    queryKey: ['catalog', 'category-tree'],
    queryFn: () => catalogApi.categoryTree(),
    staleTime: 5 * 60_000,
  });

  const quickCategories = (categoryTree ?? []).filter((c) => c.isActive !== false).slice(0, 6);

  // Load history from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(HISTORY_KEY);
      if (stored) {
        setHistory(JSON.parse(stored));
      }
    } catch {
      // ignore
    }
  }, []);

  // Save to history helper
  const saveToHistory = (term: string) => {
    const clean = term.trim();
    if (!clean) return;
    setHistory((prev) => {
      const updated = [clean, ...prev.filter((item) => item.toLowerCase() !== clean.toLowerCase())].slice(0, MAX_HISTORY);
      try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
      } catch {
        // ignore
      }
      return updated;
    });
  };

  const removeHistoryItem = (term: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setHistory((prev) => {
      const updated = prev.filter((item) => item !== term);
      try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
      } catch {
        // ignore
      }
      return updated;
    });
  };

  const clearHistory = (e: React.MouseEvent) => {
    e.stopPropagation();
    setHistory([]);
    try {
      localStorage.removeItem(HISTORY_KEY);
    } catch {
      // ignore
    }
  };

  // Debounce input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 250);
    return () => clearTimeout(handler);
  }, [query]);

  // Query autocomplete API
  const { data: results, isLoading } = useQuery<SearchAutocompleteDto>({
    queryKey: ['search-autocomplete', debouncedQuery],
    queryFn: () => catalogApi.autocomplete(debouncedQuery),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
  });

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const clean = query.trim();
    if (clean) {
      saveToHistory(clean);
      setIsOpen(false);
      if (onCloseMobile) onCloseMobile();
      navigate(`/urunler?search=${encodeURIComponent(clean)}`);
    } else {
      navigate('/urunler');
    }
  };

  const handleSelectTerm = (term: string) => {
    setQuery(term);
    saveToHistory(term);
    setIsOpen(false);
    if (onCloseMobile) onCloseMobile();
    navigate(`/urunler?search=${encodeURIComponent(term)}`);
  };

  const handleSelectProduct = (slug: string, title: string) => {
    saveToHistory(title);
    setIsOpen(false);
    if (onCloseMobile) onCloseMobile();
    navigate(`/urun/${slug}`);
  };

  const handleSelectCategory = (slug: string, name: string) => {
    saveToHistory(name);
    setIsOpen(false);
    if (onCloseMobile) onCloseMobile();
    navigate(`/kategori/${slug}`);
  };

  const handleSelectBrand = (slug: string, name: string) => {
    saveToHistory(name);
    setIsOpen(false);
    if (onCloseMobile) onCloseMobile();
    navigate(`/marka/${slug}`);
  };

  const hasResults =
    results &&
    (results.products.length > 0 ||
      results.categories.length > 0 ||
      results.brands.length > 0 ||
      results.suggestions.length > 0);

  const showDropdown =
    isOpen && (debouncedQuery.length >= 2 || history.length > 0 || quickCategories.length > 0);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Search Input Bar */}
      <form onSubmit={handleSearchSubmit} className="relative">
        <label htmlFor="search-input" className="sr-only">
          Arama
        </label>
        <div className="relative flex items-center">
          <input
            id="search-input"
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            autoComplete="off"
            className="w-full rounded-xl border border-ink-200 bg-ink-50/60 py-2 pl-9 pr-9 text-sm text-ink-900 transition placeholder:text-ink-400 focus:border-brand-500 focus:bg-white focus:outline-hidden focus:ring-2 focus:ring-brand-500/20"
          />
          <Search className="pointer-events-none absolute left-3 h-4 w-4 text-ink-400" />
          {query ? (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setIsOpen(false);
              }}
              className="absolute right-2.5 rounded-full p-0.5 text-ink-400 hover:bg-ink-200 hover:text-ink-700 transition"
              aria-label="Aramayı temizle"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      </form>

      {/* Autocomplete Dropdown Popover */}
      {showDropdown && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[85vh] overflow-y-auto rounded-2xl border border-ink-200 bg-white p-4 shadow-xl animate-in fade-in zoom-in-95 duration-150">
          {/* Query < 2: Show History & Real Store Categories */}
          {debouncedQuery.length < 2 ? (
            <div className="space-y-4">
              {/* Search History */}
              {history.length > 0 && (
                <div>
                  <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-ink-500">
                    <span className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" /> Son Aramalarınız
                    </span>
                    <button
                      type="button"
                      onClick={clearHistory}
                      className="text-ink-400 hover:text-red-500 transition normal-case font-normal"
                    >
                      Geçmişi Temizle
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {history.map((term, i) => (
                      <span
                        key={i}
                        onClick={() => handleSelectTerm(term)}
                        className="group inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-ink-50 px-2.5 py-1 text-xs text-ink-700 cursor-pointer hover:border-brand-300 hover:bg-brand-50/50 transition"
                      >
                        <span>{term}</span>
                        <button
                          type="button"
                          onClick={(e) => removeHistoryItem(term, e)}
                          className="rounded-full p-0.5 text-ink-400 hover:bg-ink-200 hover:text-red-500 transition"
                          title="Sil"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Store Categories */}
              {quickCategories.length > 0 && (
                <div>
                  <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-500">
                    <Layers className="h-3.5 w-3.5 text-brand-600" /> Hızlı Kategoriler
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {quickCategories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => handleSelectCategory(cat.slug, cat.name)}
                        className="inline-flex items-center gap-1 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:border-brand-500 hover:text-brand-600 transition cursor-pointer"
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Query >= 2: Show Live Search Results */
            <div className="space-y-4">
              {isLoading ? (
                <div className="py-8 text-center text-sm text-ink-400">
                  Aranıyor...
                </div>
              ) : !hasResults ? (
                <div className="py-6 text-center">
                  <p className="text-sm font-medium text-ink-900">Sonuç bulunamadı</p>
                  <p className="mt-1 text-xs text-ink-500">
                    "{query}" ile eşleşen ürün, marka veya kategori bulunamadı. Lütfen farklı kelimeler deneyin.
                  </p>
                </div>
              ) : (
                <>
                  {/* Category & Brand Matches */}
                  {(results.categories.length > 0 || results.brands.length > 0) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-3 border-b border-ink-100">
                      {results.categories.length > 0 && (
                        <div>
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-400 flex items-center gap-1">
                            <Layers className="h-3.5 w-3.5" /> Kategoriler
                          </p>
                          <div className="space-y-1">
                            {results.categories.map((cat) => (
                              <button
                                key={cat.id}
                                type="button"
                                onClick={() => handleSelectCategory(cat.slug, cat.name)}
                                className="w-full text-left flex items-center justify-between rounded-lg px-2.5 py-1.5 text-sm font-medium text-ink-800 hover:bg-ink-50 hover:text-brand-600 transition"
                              >
                                <span>{cat.name}</span>
                                <span className="text-xs text-ink-400">({cat.productCount} ürün)</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {results.brands.length > 0 && (
                        <div>
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-400 flex items-center gap-1">
                            <Tag className="h-3.5 w-3.5" /> Markalar
                          </p>
                          <div className="space-y-1">
                            {results.brands.map((b) => (
                              <button
                                key={b.id}
                                type="button"
                                onClick={() => handleSelectBrand(b.slug, b.name)}
                                className="w-full text-left flex items-center justify-between rounded-lg px-2.5 py-1.5 text-sm font-medium text-ink-800 hover:bg-ink-50 hover:text-brand-600 transition"
                              >
                                <span>{b.name}</span>
                                <ArrowRight className="h-3.5 w-3.5 text-ink-400" />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Product Matches */}
                  {results.products.length > 0 && (
                    <div>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-400">
                        Ürünler ({results.products.length})
                      </p>
                      <div className="space-y-2">
                        {results.products.map((product) => (
                          <div
                            key={product.id}
                            onClick={() => handleSelectProduct(product.slug, product.title)}
                            className="flex items-center gap-3 p-2 rounded-xl hover:bg-ink-50 cursor-pointer transition"
                          >
                            <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-ink-100 border border-ink-200">
                              {product.primaryImageUrl ? (
                                <img
                                  src={product.primaryImageUrl}
                                  alt={product.title}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center text-ink-300">
                                  <Search className="h-5 w-5" />
                                </div>
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-ink-900 truncate">
                                {product.title}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-ink-500">
                                {product.brandName && <span>{product.brandName}</span>}
                                {product.sku && (
                                  <span className="font-mono text-ink-400">SKU: {product.sku}</span>
                                )}
                              </div>
                            </div>

                            <div className="text-right shrink-0">
                              <p className="text-sm font-semibold text-ink-900">
                                {money(product.price)}
                              </p>
                              {product.compareAtPrice && product.compareAtPrice > product.price && (
                                <p className="text-xs text-ink-400 line-through">
                                  {money(product.compareAtPrice)}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Keyword Suggestions */}
                  {results.suggestions.length > 0 && (
                    <div className="pt-2 border-t border-ink-100">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-xs text-ink-400 mr-1">Öneriler:</span>
                        {results.suggestions.map((sug, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => handleSelectTerm(sug)}
                            className="rounded-md bg-ink-100 px-2 py-0.5 text-xs text-ink-700 hover:bg-brand-50 hover:text-brand-600 transition"
                          >
                            {sug}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* View All Results Button */}
                  <div className="pt-2 border-t border-ink-100 text-center">
                    <button
                      type="button"
                      onClick={() => handleSearchSubmit()}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 hover:text-brand-700 transition"
                    >
                      "{query}" için tüm sonuçları gör <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
